import React, { useState, useContext, useRef, useEffect, useMemo } from 'react';
import { TicketContext } from '../../../app/providers/TicketContext';
import { DebugContext } from '../../../app/providers/DebugContext';
import { PanelContext } from '../../../app/providers/PanelContext';
import * as FaIcons from 'react-icons/fa';
import { FiActivity, FiFileText } from 'react-icons/fi';
import { FaStar, FaRegStar, FaTimes, FaTrashAlt } from 'react-icons/fa';
import useStarredEvents from './useStarredEvents';
import StarredEventPreview from './StarredEventPreview';
import StarredWorkspace from './StarredWorkspace';

// Modal component to be used for search inputs
const Modal = ({ isOpen, title, onClose, onSubmit }) => {
  const [searchInput, setSearchInput] = useState(''); // Search input state

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="w-full max-w-sm bg-white rounded border border-gray-200 shadow-xl p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-800 leading-tight">{title}</h2>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Arama terimi..."
          className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          autoFocus
        />
        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 transition-colors"
          >
            İptal
          </button>
          <button
            onClick={() => onSubmit(searchInput)}
            className="px-3 py-1.5 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            Ara
          </button>
        </div>
      </div>
    </div>
  );
};

// Recursive Toolbar Item Component for nested dropdowns
const ToolbarItem = ({ button, index, onExecute, onModalOpen, currentInputFieldRef, currentInputField }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  
  const IconComponent = FaIcons[button.iconName] || FaIcons.FaInfoCircle;
  
  // Has nested toolbar items?
  const hasNestedItems = button.toolbar && Array.isArray(button.toolbar) && button.toolbar.length > 0;
  
  // Check if button is a label (inactive)
  const isLabel = button.type === 'label';
  
  // Get background color from bgcolor field or use default
  // Dark toolbar theme: default is a subtle translucent chip
  const defaultBgColor = 'rgba(255,255,255,0.06)';
  const buttonBgColor = button.bgcolor || defaultBgColor;
  
  // Calculate dropdown position when opening - viewport içinde tut
  useEffect(() => {
    if (isDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const dropdownWidth = 180; // min-w-[180px]
      const dropdownHeight = 400; // max-h-[400px]
      
      let top = rect.bottom + 4;
      let left = rect.left;
      
      // Viewport dışına çıkmaması için ayarla
      if (left + dropdownWidth > viewportWidth) {
        left = viewportWidth - dropdownWidth - 10;
      }
      if (left < 10) {
        left = 10;
      }
      
      if (top + dropdownHeight > viewportHeight) {
        top = rect.top - dropdownHeight - 4;
      }
      if (top < 10) {
        top = 10;
      }
      
      setDropdownPosition({ top, left });
    }
  }, [isDropdownOpen]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  
  const handleMouseEnter = () => {
    // Label butonları için dropdown açma
    if (isLabel) return;
    
    if (hasNestedItems) {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      // Open dropdown immediately on hover
      setIsDropdownOpen(true);
    }
  };
  
  const handleMouseLeave = () => {
    if (hasNestedItems) {
      // Delay closing to allow moving to dropdown
      hoverTimeoutRef.current = setTimeout(() => {
        setIsDropdownOpen(false);
      }, 200);
    }
  };
  
  const handleDropdownMouseEnter = () => {
    // Cancel closing when mouse enters dropdown
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };
  
  const handleDropdownMouseLeave = () => {
    // Close when mouse leaves dropdown
    hoverTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200);
  };
  
  const handleClick = () => {
    // Label butonları için hiçbir şey yapma
    if (isLabel) return;
    
    if (!hasNestedItems) {
      // Execute the action only if no nested items
      if (button.type === 'script') {
        const drcValue = typeof button.DRC === 'string' ? button.DRC : String(button.DRC || '');
        const eventData = {
          inputName: currentInputFieldRef.current?.FNAME || currentInputField?.FNAME || 'message',
          message: `-${drcValue}`,
          data: JSON.stringify(button.data || {})
        };
        onExecute(eventData);
      } else {
        onModalOpen(button.DRCDESC, button.DRC);
      }
    }
  };
  
  return (
    <>
      <div 
        className="inline-block relative z-10" 
        ref={buttonRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold rounded-xl transition-colors duration-150 whitespace-nowrap ${
            isLabel 
              ? 'text-slate-300/70 cursor-default border-0' 
              : 'text-slate-100 border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/15 active:bg-white/10'
          }`}
          style={{
            backgroundColor: isLabel ? 'transparent' : buttonBgColor,
            ...(isLabel ? {} : { 
              '--hover-bg': button.bgcolor ? buttonBgColor : 'rgba(255,255,255,0.10)' 
            })
          }}
          onClick={isLabel ? undefined : handleClick}
          disabled={isLabel}
          onMouseEnter={isLabel ? undefined : (e) => {
            // Koyu tema: hover'da beyaza çekme (yazı kayboluyor).
            // Eğer özel bgcolor yoksa, yarı saydam beyaz kullan.
            if (!isLabel && !button.bgcolor) {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.10)';
            }
          }}
          onMouseLeave={isLabel ? undefined : (e) => {
            if (!isLabel && !button.bgcolor) {
              e.currentTarget.style.backgroundColor = buttonBgColor;
            }
          }}
        >
          {!isLabel && <IconComponent size={12} className="text-slate-200" />}
          {button.DRCDESC && (
            <span className="leading-tight">
              {typeof button.DRCDESC === 'string' ? button.DRCDESC : String(button.DRCDESC)}
            </span>
          )}
          {!isLabel && hasNestedItems && (
            <FaIcons.FaChevronDown 
              size={9} 
              className={`text-slate-300/80 transition-transform duration-150 ${isDropdownOpen ? 'rotate-180' : ''}`}
            />
          )}
        </button>
      </div>
      
      {/* Fixed Dropdown Menu - Renders outside toolbar using portal */}
      {hasNestedItems && isDropdownOpen && (
        <div 
          ref={dropdownRef}
          className="fixed bg-white rounded-2xl border border-slate-200 shadow-xl min-w-[200px] max-h-[420px] overflow-y-auto overflow-x-visible z-[10000] py-1 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            maxWidth: 'calc(100vw - 20px)',
          }}
          onMouseEnter={handleDropdownMouseEnter}
          onMouseLeave={handleDropdownMouseLeave}
        >
          {button.toolbar.map((nestedButton, nestedIndex) => (
            <NestedToolbarItem
              key={`nested-${button.DRC}-${nestedIndex}`}
              button={nestedButton}
              index={nestedIndex}
              onExecute={onExecute}
              onModalOpen={onModalOpen}
              currentInputFieldRef={currentInputFieldRef}
              currentInputField={currentInputField}
              level={1}
              onClose={() => setIsDropdownOpen(false)}
            />
          ))}
        </div>
      )}
    </>
  );
};

// Nested Toolbar Item Component (for sub-levels)
const NestedToolbarItem = ({ button, index, onExecute, onModalOpen, currentInputFieldRef, currentInputField, level = 0, onClose }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  
  const IconComponent = FaIcons[button.iconName] || FaIcons.FaInfoCircle;
  
  // Has nested toolbar items?
  const hasNestedItems = button.toolbar && Array.isArray(button.toolbar) && button.toolbar.length > 0;
  
  // Check if button is a label (inactive)
  const isLabel = button.type === 'label';
  
  // Get background color from bgcolor field or use default
  const defaultBgColor = '#ffffff'; // white for dropdown items
  const buttonBgColor = button.bgcolor || defaultBgColor;
  
  // Calculate dropdown position when opening - viewport içinde tut
  useEffect(() => {
    if (isDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const dropdownWidth = 180; // min-w-[180px]
      const dropdownHeight = 400; // max-h-[400px]
      
      let top = rect.top;
      let left = rect.right + 4;
      
      // Viewport dışına çıkmaması için ayarla
      if (left + dropdownWidth > viewportWidth) {
        // Sağa sığmıyorsa sola aç
        left = rect.left - dropdownWidth - 4;
      }
      if (left < 10) {
        left = 10;
      }
      
      if (top + dropdownHeight > viewportHeight) {
        top = viewportHeight - dropdownHeight - 10;
      }
      if (top < 10) {
        top = 10;
      }
      
      setDropdownPosition({ top, left });
    }
  }, [isDropdownOpen]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  
  const handleMouseEnter = () => {
    // Label butonları için dropdown açma
    if (isLabel) return;
    
    if (hasNestedItems) {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      // Open dropdown on hover
      setIsDropdownOpen(true);
    }
  };
  
  const handleMouseLeave = () => {
    if (hasNestedItems) {
      // Delay closing to allow moving to dropdown
      hoverTimeoutRef.current = setTimeout(() => {
        setIsDropdownOpen(false);
      }, 200);
    }
  };
  
  const handleDropdownMouseEnter = () => {
    // Cancel closing when mouse enters dropdown
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };
  
  const handleDropdownMouseLeave = () => {
    // Close when mouse leaves dropdown
    hoverTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200);
  };
  
  const handleClick = () => {
    // Label butonları için hiçbir şey yapma
    if (isLabel) return;
    
    if (!hasNestedItems) {
      // Execute the action and close all dropdowns
      if (button.type === 'script') {
        const drcValue = typeof button.DRC === 'string' ? button.DRC : String(button.DRC || '');
        const eventData = {
          inputName: currentInputFieldRef.current?.FNAME || currentInputField?.FNAME || 'message',
          message: `-${drcValue}`,
          data: JSON.stringify(button.data || {})
        };
        onExecute(eventData);
      } else {
        onModalOpen(button.DRCDESC, button.DRC);
      }
      
      // Close all parent dropdowns
      if (onClose) onClose();
    }
  };
  
  return (
    <>
      <button
        ref={buttonRef}
        className={`w-full flex items-center justify-between px-2.5 py-1.5 text-[10.5px] text-left rounded overflow-visible relative transition-colors duration-100 ${
          isLabel 
            ? 'text-gray-400 cursor-default' 
            : 'text-gray-700 hover:bg-blue-50 group'
        }`}
        style={{
          backgroundColor: isLabel ? 'transparent' : buttonBgColor
        }}
        onClick={isLabel ? undefined : handleClick}
        disabled={isLabel}
        onMouseEnter={isLabel ? undefined : handleMouseEnter}
        onMouseLeave={isLabel ? undefined : handleMouseLeave}
        onMouseOver={isLabel ? undefined : (e) => {
          if (!isLabel && !button.bgcolor) {
            e.currentTarget.style.backgroundColor = '#eff6ff';
          }
        }}
        onMouseOut={isLabel ? undefined : (e) => {
          if (!isLabel && !button.bgcolor) {
            e.currentTarget.style.backgroundColor = buttonBgColor;
          }
        }}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {!isLabel && <IconComponent size={11} className="text-gray-500 group-hover:text-blue-600 flex-shrink-0 transition-colors" />}
          <span className={`whitespace-nowrap truncate ${isLabel ? 'font-normal' : 'font-medium'}`}>
            {typeof button.DRCDESC === 'string' 
              ? button.DRCDESC 
              : typeof button.DRC === 'string' 
                ? button.DRC 
                : String(button.DRCDESC || button.DRC || '')}
          </span>
        </div>
        {!isLabel && hasNestedItems && (
          <FaIcons.FaChevronRight 
            size={9} 
            className="ml-2 flex-shrink-0 text-gray-400 group-hover:text-blue-600 transition-colors"
          />
        )}
      </button>
      
      {/* Fixed Sub-nested Dropdown Menu */}
      {hasNestedItems && isDropdownOpen && (
        <div 
          ref={dropdownRef}
          className="fixed bg-white rounded border border-gray-200 shadow-lg min-w-[180px] max-h-[400px] overflow-y-auto overflow-x-visible py-0.5 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            zIndex: 10000 + level + 1, // Her seviye için z-index artır
            maxWidth: 'calc(100vw - 20px)',
          }}
          onMouseEnter={handleDropdownMouseEnter}
          onMouseLeave={handleDropdownMouseLeave}
        >
          {button.toolbar.map((subNestedButton, subNestedIndex) => (
            <NestedToolbarItem
              key={`subnested-${button.DRC}-${subNestedIndex}`}
              button={subNestedButton}
              index={subNestedIndex}
              onExecute={onExecute}
              onModalOpen={onModalOpen}
              currentInputFieldRef={currentInputFieldRef}
              currentInputField={currentInputField}
              level={level + 1}
              onClose={() => {
                setIsDropdownOpen(false);
                if (onClose) onClose();
              }}
            />
          ))}
        </div>
      )}
    </>
  );
};

const RightPanelToolbar = () => {
  const { SendEvent, rightPanelToolbarElements, currentInputField, isSendingEvent, selectedTicketId } = useContext(TicketContext);
  const { isDebugMode, toggleDebugMode } = useContext(DebugContext);
  const { isLeftPanelCollapsed, setIsLeftPanelCollapsed, requestDetailView } = useContext(PanelContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [currentDRC, setCurrentDRC] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadingUI, setShowLoadingUI] = useState(false);
  const loadingTimeoutRef = useRef(null);
  const [showStarredPanel, setShowStarredPanel] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const { starredEvents, unstar, clearAll } = useStarredEvents();
  
  // currentInputField'ı korumak için ref kullan
  const currentInputFieldRef = useRef(currentInputField);

  // Toolbar elemanları içinde type === "header" varsa üstte tek başlık olarak göster,
  // ve buton listesinden çıkar.
  const { toolbarHeader, toolbarItems } = useMemo(() => {
    const items = Array.isArray(rightPanelToolbarElements) ? rightPanelToolbarElements : [];
    const header = items.find((it) => String(it?.type || '').toLowerCase() === 'header') || null;
    const filtered = header ? items.filter((it) => it !== header) : items;
    return { toolbarHeader: header, toolbarItems: filtered };
  }, [rightPanelToolbarElements]);
  // currentInputField değiştiğinde ref'i güncelle
  useEffect(() => {
    if (currentInputField && currentInputField.FNAME) {
      currentInputFieldRef.current = currentInputField;
    }
  }, [currentInputField, rightPanelToolbarElements]);

  const handleModalOpen = (DRCDESC, DRC) => {
    setModalTitle(typeof DRCDESC === 'string' ? DRCDESC : String(DRCDESC || ''));
    setCurrentDRC(typeof DRC === 'string' ? DRC : String(DRC || ''));
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (searchInput) => {
    setIsModalOpen(false);
    const drcValue = typeof currentDRC === 'string' ? currentDRC : String(currentDRC || '');
    const eventData = {
      inputName: currentInputFieldRef.current?.FNAME || currentInputField?.FNAME || 'message',
      message: `-${drcValue} "${searchInput}"`
    };
    setIsLoading(true);
    loadingTimeoutRef.current = setTimeout(() => setShowLoadingUI(true), 1000);
    try {
      const response = await SendEvent(eventData, [], 'RightPanelToolbar');
      return response;
    } finally {
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
      setShowLoadingUI(false);
      setIsLoading(false);
    }
  };
    
  const handleExecute = async (eventData) => {
    console.log(eventData);
    setIsLoading(true);
    loadingTimeoutRef.current = setTimeout(() => setShowLoadingUI(true), 1000);
    try {
      const response = await SendEvent(eventData, [], 'RightPanelToolbar');
      console.log(response);
      return response;
    } finally {
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
      setShowLoadingUI(false);
      setIsLoading(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, []);

  return (
    <div
      className={`relative border-b border-[#0e1626] overflow-visible ${(isSendingEvent || isLoading) ? 'pointer-events-none opacity-60' : ''}`}
      style={{ backgroundColor: '#212835' }}
    >
      <div className="px-2.5 py-2">
        {/* Üst satır: Header */}
        {toolbarHeader && (
          <div className="mb-1.5 flex items-center gap-2 px-1">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-200">
              {(() => {
                const Icon = FaIcons[toolbarHeader.iconName] || FaIcons.FaInfoCircle;
                return <Icon className="text-[13px]" />;
              })()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11.5px] font-semibold text-slate-100 truncate">
                {toolbarHeader.DRCDESC || toolbarHeader.DRC || 'Header'}
              </div>
            </div>
            {selectedTicketId && (
              <div className="flex-shrink-0 text-[10px] font-semibold text-slate-300/90">
                #{selectedTicketId}
              </div>
            )}
          </div>
        )}

        {/* Alt satır: Butonlar + Debug */}
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto overflow-y-visible flex-nowrap pr-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {toolbarItems && toolbarItems.length > 0 ? (
              toolbarItems.map((button, index) => (
                <ToolbarItem
                  key={`toolbar-${button.DRC}-${index}`}
                  button={button}
                  index={index}
                  onExecute={handleExecute}
                  onModalOpen={handleModalOpen}
                  currentInputFieldRef={currentInputFieldRef}
                  currentInputField={currentInputField}
                /> 
              ))
            ) : (
              <p className="text-slate-400 text-[11px] px-2 font-medium">Toolbar öğesi bulunmuyor</p>
            )}
          </div>

          {/* Starred Events Buttons + Panel Toggle */}
          <div className="flex flex-shrink-0 items-center gap-1">
            {/* Detail View */}
            <button
              onClick={() => requestDetailView?.(selectedTicketId, 'right-toolbar')}
              disabled={!selectedTicketId}
              className={`inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11px] font-semibold transition-colors border ${
                selectedTicketId
                  ? 'bg-white/5 border-white/10 hover:bg-emerald-500/15 hover:border-emerald-400/30 text-slate-100'
                  : 'bg-white/5 border-white/10 text-slate-500 cursor-not-allowed'
              }`}
              title={selectedTicketId ? 'Detail View aç' : 'Önce ticket seçin'}
            >
              <FiFileText className="text-[12px]" />
              <span className="hidden sm:inline">Detail View</span>
            </button>

            {/* Toggle Left Panel */}
            <button
              onClick={() => setIsLeftPanelCollapsed((p) => !p)}
              className={`inline-flex items-center justify-center rounded-xl p-1.5 text-slate-200 transition-colors border ${
                isLeftPanelCollapsed
                  ? 'bg-blue-600/20 border-blue-500/30 hover:bg-blue-600/30'
                  : 'bg-white/5 border-white/10 hover:bg-white/10'
              }`}
              title={isLeftPanelCollapsed ? 'Sol Paneli Aç' : 'Sol Paneli Kapat'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ transition: 'transform 0.3s', transform: isLeftPanelCollapsed ? 'scaleX(-1)' : 'none' }}
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
                <polyline points="14 9 17 12 14 15" />
              </svg>
            </button>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            {/* Panel (liste görünümü) */}
            <button
              onClick={() => setShowStarredPanel(true)}
              className="relative inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11px] font-semibold transition-colors border bg-white/5 border-white/10 hover:bg-amber-500/15 hover:border-amber-400/30 text-slate-100"
              title="Yıldızlı eventler (liste)"
            >
              <FaStar className="text-[12px] text-amber-400" />
              {starredEvents.length > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 text-[9px] font-bold text-slate-900">
                  {starredEvents.length > 9 ? '9+' : starredEvents.length}
                </span>
              )}
            </button>
            {/* Workspace */}
            <button
              onClick={() => setShowWorkspace(true)}
              className="inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11px] font-semibold transition-colors border bg-white/5 border-white/10 hover:bg-indigo-500/15 hover:border-indigo-400/30 text-slate-100"
              title="Analiz Workspace"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <rect x="3" y="3" width="7" height="7" rx="1.5" />
                <rect x="14" y="3" width="7" height="7" rx="1.5" />
                <rect x="3" y="14" width="7" height="7" rx="1.5" />
                <rect x="14" y="14" width="7" height="7" rx="1.5" />
              </svg>
            </button>
          </div>

          {/* Debug Mode Toggle */}
          <div className="flex-shrink-0">
            <button
              onClick={toggleDebugMode}
              className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[11px] font-semibold transition-colors border ${
                isDebugMode
                  ? 'bg-blue-600 text-white border-blue-500/60'
                  : 'bg-white/5 text-slate-100 border-white/10 hover:bg-white/10'
              }`}
              title={isDebugMode ? 'Debug Mode: ON' : 'Debug Mode: OFF'}
            >
              <FiActivity className={`text-[12px] ${isDebugMode ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">Debug</span>
            </button>
          </div>
        </div>
      </div>

      {/* Render the modal when needed */}
      <Modal
        isOpen={isModalOpen}
        title={modalTitle}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleModalSubmit}
      />

      {/* 🗂 Starred Workspace */}
      {showWorkspace && (
        <StarredWorkspace
          starredEvents={starredEvents}
          unstar={unstar}
          clearAll={clearAll}
          onClose={() => setShowWorkspace(false)}
        />
      )}

      {/* ⭐ Starred Events Panel */}
      {showStarredPanel && (
        <div
          className="fixed inset-0 z-[99990] flex"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowStarredPanel(false); }}
        >
          {/* Slide-in panel from right */}
          <div
            className="relative ml-auto flex h-full w-full max-w-sm flex-col"
            style={{
              background: 'linear-gradient(160deg,#0f1824 0%,#111a27 100%)',
              borderLeft: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '-16px 0 48px rgba(0,0,0,0.6)',
              animation: 'slideInRight 0.22s cubic-bezier(0.22,1,0.36,1)',
            }}
          >
            {/* Panel Header */}
            <div
              className="flex items-center justify-between border-b px-4 py-3.5"
              style={{ borderColor: 'rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)' }}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="flex h-7 w-7 items-center justify-center rounded-lg"
                  style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)' }}
                >
                  <FaStar className="text-amber-400" style={{ fontSize: 13 }} />
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-slate-100">Starred Events</div>
                  <div className="text-[10px] text-slate-500">
                    {starredEvents.length === 0 ? 'Henüz yıldızlı event yok' : `${starredEvents.length} event yıldızlandı`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {starredEvents.length > 0 && (
                  <button
                    onClick={() => { if (window.confirm('Tüm yıldızlı eventler temizlensin mi?')) clearAll(); }}
                    className="flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-medium text-red-400 transition hover:bg-red-500/10"
                    style={{ borderColor: 'rgba(248,113,113,0.2)' }}
                    title="Tümünü temizle"
                  >
                    <FaTrashAlt style={{ fontSize: 10 }} />
                    <span>Temizle</span>
                  </button>
                )}
                <button
                  onClick={() => setShowStarredPanel(false)}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition hover:bg-white/8 hover:text-slate-200"
                >
                  <FaTimes style={{ fontSize: 13 }} />
                </button>
              </div>
            </div>

            {/* Event List */}
            <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.08) transparent' }}>
              {starredEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 py-16 px-6">
                  <div
                    className="flex h-14 w-14 items-center justify-center rounded-2xl"
                    style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.15)' }}
                  >
                    <FaRegStar className="text-amber-400/50" style={{ fontSize: 22 }} />
                  </div>
                  <div className="text-center">
                    <div className="text-[13px] font-medium text-slate-300">Yıldızlı event yok</div>
                    <div className="mt-1 text-[11px] text-slate-600">
                      Event üzerindeki ⭐ ikonuna tıklayarak<br />önemli eventleri kaydedin.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col">
                  {starredEvents.map((event, idx) => {
                    const title = event.FNAME || event.TDESC || '—';
                    const date = event.ANDAT
                      ? new Date(event.ANDAT).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                      : null;
                    const starredDate = new Date(event.starredAt).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

                    return (
                      <div
                        key={String(event.EVEID)}
                        className="group relative flex flex-col gap-1.5 px-4 py-3.5 transition-colors hover:bg-white/4"
                        style={{ borderBottom: idx < starredEvents.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}
                      >
                        {/* Amber left accent */}
                        <div
                          className="absolute left-0 top-0 h-full w-[3px] rounded-r-full opacity-0 transition-opacity group-hover:opacity-100"
                          style={{ background: 'linear-gradient(to bottom, #fbbf24, #f59e0b)' }}
                        />

                        {/* Top row: title + unstar button */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex min-w-0 flex-1 items-center gap-2">
                            <FaStar className="mt-0.5 flex-shrink-0 text-amber-400" style={{ fontSize: 11 }} />
                            <span className="truncate text-[12px] font-semibold text-slate-100">{title}</span>
                          </div>
                          <button
                            onClick={() => unstar(event.EVEID)}
                            className="flex-shrink-0 rounded-md p-1 text-amber-500/60 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-amber-500/10 hover:text-amber-400"
                            title="Yıldızı kaldır"
                          >
                            <FaTimes style={{ fontSize: 11 }} />
                          </button>
                        </div>

                        {/* TDESC */}
                        {event.TDESC && event.TDESC !== title && (
                          <div className="truncate text-[11px] text-slate-400">{event.TDESC}</div>
                        )}

                        {/* FVALUE — birebir preview */}
                        {event.FVALUE && (
                          <StarredEventPreview FVALUE={event.FVALUE} FNAME={event.FNAME} />
                        )}

                        {/* Bottom row: meta info */}
                        <div className="flex items-center gap-3 text-[9.5px] text-slate-600">
                          {event.TICID && (
                            <span className="flex items-center gap-1">
                              <span
                                className="rounded px-1.5 py-0.5 font-mono font-semibold text-slate-500"
                                style={{ background: 'rgba(255,255,255,0.05)' }}
                              >
                                #{event.TICID}
                              </span>
                            </span>
                          )}
                          {date && <span>{date}</span>}
                          <span className="ml-auto flex items-center gap-1 text-amber-600/60">
                            <FaStar style={{ fontSize: 8 }} />
                            {starredDate}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {starredEvents.length > 0 && (
              <div
                className="border-t px-4 py-3 text-center text-[10px] text-slate-600"
                style={{ borderColor: 'rgba(255,255,255,0.06)' }}
              >
                Eventler cihazınızda yerel olarak saklanır
              </div>
            )}
          </div>

          <style>{`
            @keyframes slideInRight {
              from { transform: translateX(100%); opacity: 0; }
              to   { transform: translateX(0);   opacity: 1; }
            }
          `}</style>
        </div>
      )}

      {/* 1sn+ süren işlemlerde profesyonel loading */}
      {showLoadingUI && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full mx-4 animate-scaleIn border border-slate-200">
            <div className="flex flex-col items-center gap-4">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                <div className="absolute inset-2 border-4 border-blue-400 rounded-full border-t-transparent animate-spin-slow"></div>
              </div>

              <div className="text-center space-y-1">
                <h3 className="text-base font-semibold text-slate-800">İşlem Yapılıyor</h3>
                <p className="text-sm text-slate-500">Lütfen bekleyin...</p>
              </div>

              <div className="flex gap-2">
                <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>

              {selectedTicketId && (
                <div className="mt-1 px-3 py-1 bg-blue-50 rounded-full border border-blue-100">
                  <span className="text-xs font-medium text-blue-700">Ticket: {selectedTicketId}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RightPanelToolbar;
